import { useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $profileFeatureStore } from '@lib/store/profile';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import type { Recipient } from '@shared/api/rest/alerts';
import { type CardDetailsResponse, type CardLimitsResponse, type UpdateLimitCardRequest, BlockReason, CardStatus } from '@shared/api/rest/cards';
import { EditRecipientsModal } from '@pages';
import { ActivateModal, BlockCardModal, CardDetailsModal, CardSidebarItems, ReportModal, RequestNewLimitsModal, RequestPinModal } from '@widgets';
import { Button } from '@shared/widgets';

export interface CardSidebarProps {
  card: CardDetailsResponse;
  limits?: CardLimitsResponse;
  loading: boolean;
  onFreezeCard: () => void;
  onActivate: () => void;
  onUnFreezeCard: () => void;
  onRemindPin: (password: string) => Promise<void>;
  onUpdateLabel: (label: string) => void;
  onUpdateLimits: (newLimits: UpdateLimitCardRequest) => void;
  onReport: (reason: BlockReason, location?: string) => void;
  onUpdateCategory: (category: string) => void;
}

export const CardSidebar = (props: CardSidebarProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showActivateModal, setShowActivateModal] = useState<boolean>(false);
  const [showPinModal, setShowPinModal] = useState<boolean>(false);
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [showLimitsModal, setShowLimitsModal] = useState<boolean>(false);
  const [showModalEditRecipients, setShowModalEditRecipients] = useState<boolean>(false);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, props.card.cardId);
  const symbol = useCurrencySymbol(props.card.currency);
  const blocked = useMemo(() => props.card.status === CardStatus.FROZEN || props.card.status === CardStatus.BLOCKED, [props.card]);

  return (
    <>
      <div>
        {cardFlow?.activateCard && props.card.status === CardStatus.INACTIVE && (
          <div className='mb-6 text-center'>
            <Button color='primary' className='min-w-48' onClick={() => setShowActivateModal(true)}>
              Activate
            </Button>
          </div>
        )}
        <CardSidebarItems
          card={props.card}
          limits={props.limits}
          openBlockModal={setShowBlockModal}
          openEditRecipientsModal={() => setShowModalEditRecipients(true)}
          openLimitsModal={() => setShowLimitsModal(true)}
          openPinModal={() => setShowPinModal(true)}
          openReportModal={() => setShowReportModal(true)}
          openDetailsModal={() => setShowDetailsModal(true)}
          showBlockModal={showBlockModal}
        />
      </div>
      <ActivateModal
        show={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        onRequest={props.onActivate}
        lastDigits={props.card.cardNumber?.slice(-4) || ''}
        cardHolder={props.card.cardHolder || ''}
        loading={props.loading}
      />
      <CardDetailsModal
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onChangeLabel={props.onUpdateLabel}
        onChangeCategory={props.onUpdateCategory}
        initial={{ label: props.card.cardLabel, category: props.card.category }}
        loading={props.loading}
      />
      <RequestPinModal
        show={showPinModal}
        onClose={() => setShowPinModal(false)}
        onRequest={props.onRemindPin}
        mobileNumber={`+${props.card.mobileAreaCode || ''}${props.card.mobileNumber || ''}`}
        loading={props.loading}
      />
      <BlockCardModal
        show={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        blocked={blocked}
        onRequest={blocked ? props.onUnFreezeCard : props.onFreezeCard}
        loading={props.loading}
      />
      <ReportModal show={showReportModal} onClose={() => setShowReportModal(false)} onSave={props.onReport} loading={props.loading} />
      <EditRecipientsModal
        show={showModalEditRecipients}
        onClose={() => setShowModalEditRecipients(false)}
        accountId={props.card.accountId}
        recipients={props.card.declinedAuthorizationSubscription?.recipientDetails as Recipient[]}
        subId={props.card.declinedAuthorizationSubscription?.subscriptionId}
      />
      {props.limits && (
        <RequestNewLimitsModal
          show={showLimitsModal}
          onClose={() => setShowLimitsModal(false)}
          limits={props.limits}
          symbol={symbol}
          onRequest={props.onUpdateLimits}
          loading={props.loading}
        />
      )}
    </>
  );
};
